import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Star, Phone, Clock, Truck, CalendarCheck, Loader2, LayoutGrid, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "@/components/app/AppLayout";
import { restApi, PRICE_RANGES, REST_SORTS } from "@/lib/restApi";
import MapView from "@/components/maps/MapView";
import { Link as RouterLink } from "react-router-dom";

const PRICE_LABEL = { "$": "Económico", "$$": "Moderado", "$$$": "Alto", "$$$$": "Premium" };

export default function Restaurants() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list");
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [sort, setSort] = useState("rating");
  const [openNow, setOpenNow] = useState(false);
  const [topRated, setTopRated] = useState(false);
  const [hasDelivery, setHasDelivery] = useState(false);
  const [hasReservations, setHasReservations] = useState(false);
  const [coords, setCoords] = useState(null);
  const [radiusKm, setRadiusKm] = useState(10);

  useEffect(() => { restApi.cuisines().then(setCuisines); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { q, country, city, cuisine, price_range: priceRange, sort,
                     open_now: openNow, top_rated: topRated, has_delivery: hasDelivery, has_reservations: hasReservations };
    if (coords) {
      params.near_lat = coords.lat;
      params.near_lng = coords.lng;
      params.radius_km = radiusKm;
    }
    restApi.list(params).then((d) => setItems(d || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [q, country, city, cuisine, priceRange, sort, openNow, topRated, hasDelivery, hasReservations, coords, radiusKm]);

  const requestGeo = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no soportada"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSort("distance");
        toast.success("Mostrando restaurantes cercanos");
      },
      (err) => toast.error(err.message || "Error de ubicación"),
      { timeout: 8000 }
    );
  };

  return (
    <AppLayout hideRightRail>
      <PageHeader
        kicker="◊ Explorar · Restaurantes"
        title="Restaurantes."
        subtitle="Descubre los mejores restaurantes cerca de ti o en cualquier ciudad del mundo. Filtra por cocina, precio y disponibilidad."
      />

      {/* Filters */}
      <div className="space-y-2 mb-4" data-testid="rest-filters">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
            <input data-testid="rest-search" placeholder="Buscar restaurante o tipo de cocina…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral" />
          </div>
          <input data-testid="rest-country" placeholder="País" value={country} onChange={(e) => setCountry(e.target.value)}
                 className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none w-32" />
          <input data-testid="rest-city" placeholder="Ciudad" value={city} onChange={(e) => setCity(e.target.value)}
                 className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none w-36" />
          <select data-testid="rest-cuisine" value={cuisine} onChange={(e) => setCuisine(e.target.value)}
                  className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
            <option value="">Cualquier cocina</option>
            {cuisines.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="rest-price" value={priceRange} onChange={(e) => setPriceRange(e.target.value)}
                  className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
            <option value="">Cualquier precio</option>
            {PRICE_RANGES.map((p) => <option key={p.id} value={p.id}>{p.id} · {PRICE_LABEL[p.id]}</option>)}
          </select>
          <select data-testid="rest-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                  className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
            {REST_SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Toggle data-testid="rest-open-now" active={openNow} onClick={() => setOpenNow((v) => !v)}>Abierto ahora</Toggle>
          <Toggle data-testid="rest-top-rated" active={topRated} onClick={() => setTopRated((v) => !v)}>Mejor valorados</Toggle>
          <Toggle data-testid="rest-delivery" active={hasDelivery} onClick={() => setHasDelivery((v) => !v)}>Delivery</Toggle>
          <Toggle data-testid="rest-reservations" active={hasReservations} onClick={() => setHasReservations((v) => !v)}>Reservas</Toggle>
          {!coords ? (
            <button onClick={requestGeo} data-testid="rest-near-me"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition">
              <MapPin className="w-3.5 h-3.5" /> Cerca de mí
            </button>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-coral text-white">
                <MapPin className="w-3.5 h-3.5" /> {coords.lat.toFixed(2)}, {coords.lng.toFixed(2)}
              </span>
              <select value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} data-testid="rest-radius"
                      className="bg-cream-card border hairline rounded-full px-3 py-1.5 text-xs outline-none">
                {[2, 5, 10, 25, 50, 100].map((r) => <option key={r} value={r}>{r} km</option>)}
              </select>
              <button onClick={() => setCoords(null)} className="text-xs text-ink-mid hover:text-coral underline">limpiar</button>
            </>
          )}
        </div>
      </div>

      {/* View toggle */}
      <div className="flex justify-end mb-3">
        <div className="inline-flex border hairline rounded-full bg-cream-card overflow-hidden text-xs">
          <button onClick={() => setView("list")} data-testid="rest-view-list"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 transition
                    ${view === "list" ? "bg-ink text-cream" : "hover:bg-cream"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Lista
          </button>
          <button onClick={() => setView("map")} data-testid="rest-view-map"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 transition
                    ${view === "map" ? "bg-ink text-cream" : "hover:bg-cream"}`}>
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </button>
        </div>
      </div>

      {/* Map view */}
      {view === "map" && (
        <MapView
          items={items}
          testid="rest-map"
          height="70vh"
          renderPopup={(it) => (
            <div className="text-sm">
              {it.photos?.[0] && (
                <img src={it.photos[0]} alt={it.name} className="w-40 h-28 object-cover rounded-lg mb-2" />
              )}
              <p className="font-medium">{it.name}</p>
              <p className="text-[11px] font-mono text-coral mb-1">{it.cuisine} · {it.price_range} · ⭐ {it.rating?.toFixed(1)}</p>
              <p className="text-xs text-ink-mid">{it.address || it.city}</p>
              <RouterLink to={`/restaurantes/${it.id}`} className="text-xs text-coral hover:underline">
                Ver detalles →
              </RouterLink>
            </div>
          )}
        />
      )}

      {/* Grid */}
      {view === "list" && (
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rest-grid">
        {loading && (
          <p className="col-span-full text-center text-sm text-ink-mid py-12 inline-flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </p>
        )}
        {!loading && items.length === 0 && (
          <p className="col-span-full text-center text-sm text-ink-mid py-12" data-testid="rest-empty">
            Sin restaurantes para esta búsqueda. Prueba ampliando el radio o quitando filtros.
          </p>
        )}
        {!loading && items.map((r) => (
          <Link key={r.id} to={`/restaurantes/${r.id}`} data-testid={`rest-${r.id}`}
                className="group border hairline bg-cream-card rounded-2xl overflow-hidden hover:shadow-lg transition">
            <div className="aspect-[4/3] bg-cream-deep relative overflow-hidden">
              {r.photos?.[0]
                ? <img src={r.photos[0]} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                : <div className="w-full h-full flex items-center justify-center font-mono text-xs uppercase tracking-wider text-ink-mid">Sin foto</div>}
              <span className="absolute top-2 right-2 bg-cream/90 backdrop-blur-sm text-ink text-[11px] font-mono px-2 py-1 rounded-full inline-flex items-center gap-1">
                <Star className="w-3 h-3 fill-coral text-coral" /> {r.rating?.toFixed(1)}
              </span>
              {r.distance_km !== null && r.distance_km !== undefined && (
                <span className="absolute bottom-2 left-2 bg-coral text-white text-[10px] font-mono px-2 py-0.5 rounded-full">
                  {r.distance_km} km
                </span>
              )}
            </div>
            <div className="p-4">
              <p className="font-mono text-[9px] uppercase tracking-wider text-coral mb-1">
                {r.cuisine} · {r.price_range} · {r.zone || r.city}
              </p>
              <h3 className="font-medium text-sm leading-snug mb-1 line-clamp-1 group-hover:text-coral transition-colors">
                {r.name}
              </h3>
              <p className="text-xs text-ink-mid line-clamp-2 mb-2">{r.description}</p>
              <div className="flex items-center gap-2 text-[11px] text-ink-mid">
                <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {r.city}</span>
                {r.has_delivery && <span title="Delivery"><Truck className="w-3 h-3 text-emerald-600" /></span>}
                {r.has_reservations && <span title="Reservas"><CalendarCheck className="w-3 h-3 text-blue-600" /></span>}
                <span>· {r.reviews_count} reseñas</span>
              </div>
            </div>
          </Link>
        ))}
      </section>
      )}
    </AppLayout>
  );
}

function Toggle({ active, onClick, children, ...rest }) {
  return (
    <button onClick={onClick} {...rest}
            className={`text-xs px-3 py-1.5 rounded-full border hairline transition
              ${active ? "bg-ink text-cream border-ink" : "bg-cream-card hover:border-coral"}`}>
      {children}
    </button>
  );
}
